/**
 * `t()` — Russian and English strings with ICU-lite formatting.
 *
 * Strings live in `src/content/strings/{ru,en}/<module>.ts` as flat records of full keys
 * (`'combat.screen.arena': 'Ристалище'`); each language index merges them and the app builds one
 * translator with {@link createTranslator}. This module imports nothing outside `src/shared`.
 *
 * Template syntax (ICU-lite):
 * - `{name}` — a parameter; numbers use the selected locale (Russian by default).
 * - `{n, plural, one {# свеча} few {# свечи} many {# свечей} other {# свечи}}` — Russian plural (`=0 {…}` exact
 *   matches allowed); `#` inside a branch is the formatted number.
 * - `{g, select, m {Он пришёл} f {Она пришла} other {…}}` — gender/enum branches (ui-ux §16.4: both branches required
 *   wherever a past-tense verb refers to a creature).
 * Branches nest. A missing parameter is left visible as `{name}`; a missing key returns the key itself and is recorded.
 */

/** A flat table of full keys → templates. */
export type StringTable = Readonly<Record<string, string>>;
/** Template parameters. */
export type TParams = Readonly<Record<string, string | number>>;
/** The `t` function carried by `AppContext.t`. */
export type TFunction = (key: string, params?: TParams) => string;
export type Language = 'ru' | 'en';
/** Russian plural categories (CLDR). */
export type PluralCategory = 'one' | 'few' | 'many' | 'other';

/**
 * Russian plural rule: integers — n%10 = 1 ∧ n%100 ≠ 11 → one; n%10 ∈ 2..4 ∧ n%100 ∉ 12..14 → few; else many;
 * non-integers → other (1 монета, 2 монеты, 5 монет, 21 монета, 1,5 монеты). Equals `Intl.PluralRules('ru-RU')`.
 */
export function pluralRu(n: number): PluralCategory {
  if (!Number.isFinite(n)) return 'other';
  if (!Number.isInteger(n)) return 'other';
  const a = Math.abs(n), m10 = a % 10, m100 = a % 100;
  if (m10 === 1 && m100 !== 11) return 'one';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'few';
  return 'many';
}

let nf: Intl.NumberFormat | null = null;
/** `ru-RU` number formatting (≤ 2 fraction digits): 12345 → "12 345" (U+00A0), 1.5 → "1,5", −3 → "−3" (U+2212). */
export function formatNumberRu(n: number): string {
  nf ??= new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
  return nf.format(n).replace('-', '−');
}

type Node =
  | string
  | { k: 'arg'; name: string }
  | { k: 'hash' }
  | { k: 'plural' | 'select'; name: string; branches: Map<string, Node[]> };

class ParseError extends Error {}

function parse(src: string): Node[] {
  let i = 0;
  const parseNodes = (inPlural: boolean, closing: boolean): Node[] => {
    const out: Node[] = [];
    let lit = '';
    const flush = (): void => { if (lit) { out.push(lit); lit = ''; } };
    while (i < src.length) {
      const c = src[i];
      if (c === '}') {
        if (!closing) throw new ParseError(`unexpected "}" at ${i}`);
        flush();
        return out;
      }
      if (c === '#' && inPlural) { flush(); out.push({ k: 'hash' }); i++; continue; }
      if (c === '{') { flush(); i++; out.push(parseArg()); continue; }
      lit += c; i++;
    }
    if (closing) throw new ParseError('unterminated "{"');
    flush();
    return out;
  };
  const skipWs = (): void => { while (i < src.length && /\s/.test(src[i])) i++; };
  const readUntil = (stop: RegExp): string => {
    const s = i;
    while (i < src.length && !stop.test(src[i])) i++;
    return src.slice(s, i).trim();
  };
  const parseArg = (): Node => {
    const name = readUntil(/[,}]/);
    if (!name) throw new ParseError(`empty argument at ${i}`);
    if (src[i] === '}') { i++; return { k: 'arg', name }; }
    i++; // ','
    const kind = readUntil(/[,}]/);
    if (kind !== 'plural' && kind !== 'select') throw new ParseError(`unknown argument type "${kind}"`);
    if (src[i] !== ',') throw new ParseError(`"${kind}" needs branches`);
    i++;
    const branches = new Map<string, Node[]>();
    for (;;) {
      skipWs();
      if (i >= src.length) throw new ParseError('unterminated branch list');
      if (src[i] === '}') { i++; break; }
      const key = readUntil(/[\s{]/);
      skipWs();
      if (src[i] !== '{') throw new ParseError(`branch "${key}" needs {…}`);
      i++;
      branches.set(key, parseNodes(kind === 'plural', true));
      i++; // '}'
    }
    if (!branches.has('other') && kind === 'select' && branches.size === 0) throw new ParseError('select without branches');
    return { k: kind, name, branches };
  };
  return parseNodes(false, false);
}

function render(nodes: readonly Node[], params: TParams | undefined, hashValue: number | null, language: Language): string {
  const number = (n: number): string => language === 'ru' ? formatNumberRu(n) : new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(n).replace('-', '−');
  let s = '';
  for (const n of nodes) {
    if (typeof n === 'string') { s += n; continue; }
    if (n.k === 'hash') { s += hashValue === null ? '#' : number(hashValue); continue; }
    const v = params?.[n.name];
    if (n.k === 'arg') {
      s += v === undefined ? `{${n.name}}` : typeof v === 'number' ? number(v) : v;
      continue;
    }
    if (v === undefined) { s += `{${n.name}}`; continue; }
    let branch: Node[] | undefined;
    if (n.k === 'plural') {
      const num = typeof v === 'number' ? v : Number(v);
      const category = language === 'ru' ? pluralRu(num) : Math.abs(num) === 1 ? 'one' : 'other';
      branch = n.branches.get(`=${num}`) ?? n.branches.get(category) ?? n.branches.get('other') ?? n.branches.get('many');
      s += branch ? render(branch, params, num, language) : '';
    } else {
      branch = n.branches.get(String(v)) ?? n.branches.get('other');
      s += branch ? render(branch, params, hashValue, language) : '';
    }
  }
  return s;
}

const cache = new Map<string, Node[]>();

/**
 * Formats one ICU-lite template. Throws on a malformed template (string modules are validated by tests, so a broken
 * template fails CI rather than a player's screen).
 */
export function formatMessage(template: string, params?: TParams, language: Language = 'ru'): string {
  let nodes = cache.get(template);
  if (!nodes) {
    try { nodes = parse(template); } catch (e) {
      throw new Error(`i18n: malformed template ${JSON.stringify(template)}: ${(e as Error).message}`);
    }
    cache.set(template, nodes);
  }
  return render(nodes, params, null, language);
}

/** Checks a template parses; returns the error message or null (string-module lint tests). */
export function validateTemplate(template: string): string | null {
  try { parse(template); return null; } catch (e) { return (e as Error).message; }
}

export interface Translator {
  /** The `t` function (`AppContext.t`). */
  readonly t: TFunction;
  /** True when the key exists. */
  has(key: string): boolean;
  /** Keys requested but missing since creation (debug overlay, tests). */
  missing(): readonly string[];
}

/** Builds a translator over a merged table. `onMissing` is called once per missing key (default: console.warn). */
export function createTranslator(table: StringTable, opts: { onMissing?: (key: string) => void; language?: Language } = {}): Translator {
  const missing = new Set<string>();
  const onMissing = opts.onMissing ?? ((key: string) => console.warn(`[i18n] missing string key "${key}"`));
  const t: TFunction = (key, params) => {
    const tpl = table[key];
    if (tpl === undefined) {
      if (!missing.has(key)) { missing.add(key); onMissing(key); }
      return key;
    }
    return formatMessage(tpl, params, opts.language ?? 'ru');
  };
  return { t, has: (key) => Object.prototype.hasOwnProperty.call(table, key), missing: () => [...missing] };
}

/** Merges string modules; throws on a duplicate key (two modules must never define the same key). */
export function mergeStringTables(...tables: StringTable[]): StringTable {
  const out: Record<string, string> = {};
  for (const tb of tables) {
    for (const [k, v] of Object.entries(tb)) {
      if (Object.prototype.hasOwnProperty.call(out, k)) throw new Error(`i18n: duplicate string key "${k}"`);
      out[k] = v;
    }
  }
  return out;
}
